/**
 * Aadhar Visibility Data Logic
 * Manages OTP verification and Aadhar number visibility
 */

import { useState, useCallback } from "react";
import { getUserData } from "../../utils/authnicate";
import {
  isAadharField,
  getAadharFieldLabel,
  maskAadhar,
  formatAadhar,
} from "../../utils/aadharMaskingUtils";

export const useAadharVisibilityData = (
  entityDocInputs,
  entityPrimaryId = 0,
) => {
  // Track which Aadhar fields are currently visible
  const [visibleAadharFields, setVisibleAadharFields] = useState({});

  // Store original (decrypted) Aadhar values from API
  const [originalAadharValues, setOriginalAadharValues] = useState({});

  // Track hide timers
  const [hideTimers, setHideTimers] = useState({});

  // OTP Modal state
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [currentAadharField, setCurrentAadharField] = useState(null);

  // Get admin email from logged-in user
  const adminEmail = getUserData()?.email || "admin@example.com";

  /**
   * Handle eye icon click - open OTP modal
   */
  const handleEyeIconClick = useCallback(
    (fieldName) => {
      const isVisible = visibleAadharFields[fieldName];

      if (isVisible) {
        // If already visible, hide it immediately
        hideAadharField(fieldName);
      } else {
        // If hidden, open OTP modal for verification
        setCurrentAadharField(fieldName);
        setOtpModalOpen(true);
      }
    },
    [visibleAadharFields],
  );

  /**
   * Handle successful OTP verification
   * @param {string} originalValue - The decrypted Aadhar value from API
   */
  const handleOtpVerifySuccess = useCallback(
    (originalValue) => {
      if (!currentAadharField) return;

      // Store the original value
      setOriginalAadharValues((prev) => ({
        ...prev,
        [currentAadharField]: originalValue,
      }));

      // Show the Aadhar field
      showAadharField(currentAadharField);

      // Close modal
      setOtpModalOpen(false);
      setCurrentAadharField(null);
    },
    [currentAadharField],
  );

  /**
   * Show Aadhar field after OTP verification
   * Auto-hide after 20 seconds
   */
  const showAadharField = useCallback(
    (fieldName) => {
      // Clear existing timer if any
      if (hideTimers[fieldName]) {
        clearTimeout(hideTimers[fieldName]);
      }

      // Mark field as visible
      setVisibleAadharFields((prev) => ({
        ...prev,
        [fieldName]: true,
      }));

      // Set timer to auto-hide after 20 seconds
      const timer = setTimeout(() => {
        setVisibleAadharFields((prev) => ({
          ...prev,
          [fieldName]: false,
        }));
        // Clear the original value from memory after hiding
        setOriginalAadharValues((prev) => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
        setHideTimers((prev) => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
      }, 20000); // 20 seconds

      setHideTimers((prev) => ({
        ...prev,
        [fieldName]: timer,
      }));
    },
    [hideTimers],
  );

  /**
   * Hide Aadhar field manually
   */
  const hideAadharField = useCallback(
    (fieldName) => {
      // Clear timer
      if (hideTimers[fieldName]) {
        clearTimeout(hideTimers[fieldName]);
      }

      // Mark field as hidden
      setVisibleAadharFields((prev) => ({
        ...prev,
        [fieldName]: false,
      }));

      // Clear the original value from memory
      setOriginalAadharValues((prev) => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });

      setHideTimers((prev) => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    },
    [hideTimers],
  );

  /**
   * Get display value for Aadhar field (masked or full)
   */
  const getAadharDisplayValue = useCallback(
    (fieldName, rawValue) => {
      if (!rawValue) return "";
      if (!isAadharField(fieldName)) return rawValue;

      const isVisible = visibleAadharFields[fieldName];

      if (isVisible && originalAadharValues[fieldName]) {
        // Show the original (decrypted) value from API
        return formatAadhar(originalAadharValues[fieldName]);
      } else {
        // Show masked Aadhar number
        return maskAadhar(rawValue);
      }
    },
    [visibleAadharFields, originalAadharValues],
  );

  /**
   * Check if Aadhar field is currently visible
   */
  const isAadharVisible = useCallback(
    (fieldName) => {
      return !!visibleAadharFields[fieldName];
    },
    [visibleAadharFields],
  );

  /**
   * Close OTP modal
   */
  const handleCloseOtpModal = useCallback(() => {
    setOtpModalOpen(false);
    setCurrentAadharField(null);
  }, []);

  /**
   * Get field label for OTP modal
   */
  const getCurrentFieldLabel = useCallback(() => {
    if (!currentAadharField) return "Aadhar Number";
    return getAadharFieldLabel(currentAadharField);
  }, [currentAadharField]);

  return {
    // State
    visibleAadharFields,
    otpModalOpen,
    currentAadharField,
    adminEmail,
    entityPrimaryId,

    // Methods
    handleEyeIconClick,
    handleOtpVerifySuccess,
    handleCloseOtpModal,
    getAadharDisplayValue,
    isAadharVisible,
    getCurrentFieldLabel,
    isAadharField,
  };
};

export default useAadharVisibilityData;
